import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { PageHeader, PhotoTitleCard, type SplitFeatureSectionPicture } from '@repo/shared-client/components/blocks'
import { createFileRoute, Link } from '@tanstack/react-router'
import allPlacesPicture from '@web/src/assets/place/place-all.webp?w=400;800;1200&format=avif;webp&as=picture'
import beachPicture from '@web/src/assets/place/place-beach.webp?w=400;800;1200&format=avif;webp&as=picture'
import campingPicture from '@web/src/assets/place/place-camping.webp?w=400;800;1200&format=avif;webp&as=picture'
import clubPicture from '@web/src/assets/place/place-club.webp?w=400;800;1200&format=avif;webp&as=picture'
import otherPicture from '@web/src/assets/place/place-other.webp?w=400;800;1200&format=avif;webp&as=picture'
import resortPicture from '@web/src/assets/place/place-resort.webp?w=400;800;1200&format=avif;webp&as=picture'
import PlacesMenu from '@web/src/features/place/places-menu/places-menu'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { useTranslation } from '@web/src/lib/intl/react'
import type { PlaceCategoryEnum } from 'types/graphql'
import { placeCategoriesIndexHeadFromLoaderData } from './index.seo'

type PlaceCategoryCountsQuery = {
  placeCategoryCounts: {
    allPlaces: number
    nudeBeach: number
    resort: number
    club: number
    camping: number
    other: number
  }
}

const PLACE_CATEGORY_COUNTS_QUERY: TypedDocumentNode<PlaceCategoryCountsQuery> = gql`
  query PlaceCategoryCountsQuery {
    placeCategoryCounts {
      allPlaces
      nudeBeach
      resort
      club
      camping
      other
    }
  }
`

export const Route = createFileRoute('/(public)/(main-layout)/place/')({
  component: PlaceCategoryTilesPage,
  head: ({ loaderData }) => placeCategoriesIndexHeadFromLoaderData(loaderData),
  loader: async ({ context }) => {
    const { queryData, queryRef } = await queryAndPreload(context, PLACE_CATEGORY_COUNTS_QUERY)
    return {
      placeCategoryCountsQueryRef: queryRef,
      placeCategoryCountsQueryData: queryData,
      placeCategoryCounts: queryData?.placeCategoryCounts ?? null,
    }
  },
})

type CategoryCardConfig = {
  id: string
  titleKey: string
  imagePicture: SplitFeatureSectionPicture
  imageY?: string | number
  count: number
  category?: PlaceCategoryEnum
  desktopSpan?: { colSpan?: number; rowSpan?: number }
}

function PlaceCategoryTilesPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const { isAtLeastRole } = useAuth()
  const placesBrowseTo = isAtLeastRole('preview') ? '/place/map' : '/place/list'
  const { data } = useReadQuery(loaderData.placeCategoryCountsQueryRef as QueryRef<PlaceCategoryCountsQuery>)
  const counts =
    (data ?? loaderData.placeCategoryCountsQueryData)?.placeCategoryCounts ?? loaderData.placeCategoryCounts

  /** Keep tile ids, categories aligned with `CATEGORY_TILES_FOR_JSON_LD` in `./index.seo.ts`. */
  const cards: CategoryCardConfig[] = [
    {
      id: 'all-places',
      titleKey: t('All Places'),
      imagePicture: allPlacesPicture,
      count: counts?.allPlaces ?? 0,
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'nude-beach',
      titleKey: t('Nude Beaches'),
      imagePicture: beachPicture,
      count: counts?.nudeBeach ?? 0,
      category: 'NUDE_BEACH',
      imageY: '62%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'resort',
      titleKey: t('Resorts'),
      imagePicture: resortPicture,
      count: counts?.resort ?? 0,
      category: 'RESORT',
      imageY: '70%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
    {
      id: 'club',
      titleKey: t('Clubs'),
      imagePicture: clubPicture,
      count: counts?.club ?? 0,
      category: 'CLUB',
      imageY: '58%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'camping',
      titleKey: t('Camping - Nude'),
      imagePicture: campingPicture,
      count: counts?.camping ?? 0,
      category: 'CAMPING',
      imageY: '100%',
      desktopSpan: { colSpan: 2, rowSpan: 2 },
    },
    {
      id: 'other',
      titleKey: t('Other'),
      imagePicture: otherPicture,
      count: counts?.other ?? 0,
      category: 'OTHER',
      imageY: '50%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
  ]

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-4 pb-10 md:px-16 md:pb-16">
      <PageHeader
        breadcrumbItems={[{ label: t('Home'), to: '/' }, { label: t('Place Categories') }]}
        highlightTitle={t('Categories')}
        preTitle={t('Place')}
        subtitle={t('Browse place categories to quickly jump into venues that match your vibe.')}
      />

      <div className="my-4">
        <PlacesMenu activeTab="categories" />
      </div>

      <div className="grid w-full auto-rows-auto grid-cols-1 gap-3 pt-4 md:auto-rows-[180px] md:grid-cols-3 md:gap-6 md:pt-0">
        {cards.map((card) => (
          <div
            className={[
              'col-span-1 row-span-1 h-full',
              (card.desktopSpan?.colSpan ?? 1) === 2 ? 'md:col-span-2' : 'md:col-span-1',
              (card.desktopSpan?.rowSpan ?? 1) === 2 ? 'md:row-span-2' : 'md:row-span-1',
            ].join(' ')}
            key={card.id}
          >
            <PhotoTitleCard
              imageAlt={t('Background image for {{title}}', { title: card.titleKey })}
              imagePicture={card.imagePicture}
              imagePositionY={card.imageY}
              rootProps={{
                as: Link,
                to: placesBrowseTo,
                search: { page: 1, ...(card.category ? { categories: [card.category] } : {}) },
                className: 'block w-full',
              }}
              subtitle={t('{{count}} Places', { count: card.count })}
              title={card.titleKey}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
